/* eslint-disable jsx-a11y/label-has-associated-control */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  User,
  Phone,
  Mail,
  Clock,
  TrendingUp,
  TrendingDown,
  Loader2,
  PhoneCall,
  Target,
  DollarSign,
  FileText,
  Users,
  CircleDot,
  MessageSquareText,
  Headset,
  Flame,
  Info,
  PhoneOff,
  Clock3,
  CalendarX,
  BadgeInfo,
  CreditCard,
  Globe,
  MonitorSmartphone,
  ChevronDown,
} from "lucide-react";
import { formatPhoneNumber } from "@/app/_module/utils/formatters";
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/app/_module/components/ui/tooltip";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";

import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

import { Button } from "@/app/_module/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/app/_module/components/ui/card";

import { useGetIndividualUserStats } from "../../hooks/use-hook";
import type { AgencyUserWithAgency } from "../../types/types";
import { useFeatureFlag } from "@/app/_module/hooks/useFeatureFlag";
import { TechficientAgentPanel } from "./techficient/TechficientAgentPanel";
import { AgentPerformanceInsights, useAgentPerformanceInsights, stagePill } from "./AgentPerformanceInsights";
import DialEfficiencyTab from "./DialEfficiencyTab";
import { AgentAssignedLeads } from "./AgentAssignedLeads";
import { AgentCallRecordings } from "./AgentCallRecordings";

interface AgentDetailsViewProps {
  agencyUser: AgencyUserWithAgency;
  agencyId: string;
  period: "6-months" | "12-months" | "year-to-date";
  startDate?: string;
  endDate?: string;
  onBack: () => void;
  backLabel?: string;
}

/**
 * Status palette — semantic only. One color per state, used consistently
 * across the hero dot, header badge, and any inline status references.
 */
const STATUS_META: Record<string, { label: string; dot: string; bg: string; text: string }> = {
  available: { label: "Available", dot: "bg-emerald-500", bg: "bg-emerald-50", text: "text-emerald-700" },
  idle:      { label: "Idle",      dot: "bg-amber-500",   bg: "bg-amber-50",   text: "text-amber-700"   },
  on_call:   { label: "On Call",   dot: "bg-blue-500",    bg: "bg-blue-50",    text: "text-blue-700"    },
  dnd:       { label: "Do Not Disturb", dot: "bg-rose-500", bg: "bg-rose-50", text: "text-rose-700"     },
  offline:   { label: "Offline",   dot: "bg-gray-400",    bg: "bg-gray-50",    text: "text-gray-600"    },
};

/**
 * Carrier color palette for the pie chart. Distinct + colorblind-friendly
 * enough for ~12 carriers; recycles past that. Matches the CarrierBreakdown
 * component's palette so an agent's slice color is consistent with the
 * agency-wide view.
 */
const CARRIER_COLORS = [
  "#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8", "#82CA9D",
  "#FFC658", "#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#E17055",
];

export const AgentDetailsView = function ({
  agencyUser,
  agencyId,
  period,
  startDate,
  endDate,
  onBack,
  backLabel,
}: AgentDetailsViewProps) {
  const backText = backLabel ? `Back to ${backLabel}` : "Back to Dashboard";
  const { data: userStats, isLoading, error } = useGetIndividualUserStats(
    agencyId,
    agencyUser.userId,
    period,
    startDate,
    endDate,
  );

  // MUST live above the early returns below — React's rules-of-hooks require
  // the call order to stay stable across renders. The Quote & Enroll panel
  // is MRFG-gated via the `quoteAndEnroll` feature flag (cascades through
  // sub-agencies), so non-MRFG agencies never see the section.
  const { enabled: quoteEnrollEnabled } = useFeatureFlag("quoteAndEnroll");
  const [accountOpen, setAccountOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"overview" | "leads">("overview");

  // Same query the insights section uses — react-query dedupes on the shared
  // key, so the hero call KPIs and the charts below cost one request total.
  const { data: insights } = useAgentPerformanceInsights(
    agencyId,
    agencyUser.userId,
    startDate,
    endDate,
    period,
  );
  const insightCore = insights?.core;
  const insightSms = insights?.sms;
  const insightHygiene = insights?.hygiene;
  // Both rates are OUTBOUND OVER OUTBOUND. Using the all-calls total meant an
  // inbound call sat in the numerator and the denominator — and inbound is
  // answered by definition — so every rate read high for anyone taking calls.
  // The Dials tile shows the same outbound figure, so the fraction is now one
  // an owner can do in their head from what is on screen.
  const outboundDials = insightCore ? Number(insightCore.dials_outbound || 0) : 0;
  const answerRate =
    outboundDials > 0
      ? Math.round((Number(insightCore!.answered_outbound || 0) / outboundDials) * 100)
      : 0;
  // The number an owner actually wants: dials that became a real conversation.
  // `answered` counts any audio at all, so on a heavy dialing day it reads ~34%
  // while the agent spoke to six people. Both are shown; neither is hidden.
  const conversationRate =
    outboundDials > 0
      ? Math.round((Number(insightCore!.conversations_outbound || 0) / outboundDials) * 100)
      : 0;
  // The time tile shows three figures because any one of them alone misleads:
  // "on the dialer" is effort, "talking" is contact, "in real conversations" is
  // the part that could actually become a sale. On a heavy day those read
  // 8h 34m / 3h 24m / 2h 30m — the middle number is what used to be labelled
  // "Dial Time" on its own, which is how an owner ended up believing an agent
  // spent three hours dialing when he spent eight.
  //
  // No dial COUNT here: the Dials tile sits directly beside this one.
  // "Talking 3h 24m" above "In real conversations 2h 30m" kept prompting the
  // same question: why is talking bigger? Because it includes every pickup,
  // machines included. So show the SUBTRACTION instead of leaving people to
  // infer it — the two child rows sum to the parent, visibly.
  const timeRows = insightCore
    ? (() => {
        const talk = Number(insightCore.talk_seconds || 0);
        const convo = Number(insightCore.conversation_seconds || 0);
        return [
          { label: "On the dialer", secs: Number(insightCore.dialer_seconds || 0), level: 0 },
          { label: "Talking", secs: talk, level: 0 },
          { label: "Real conversations (1 min+)", secs: convo, level: 1 },
          // Never negative: conversation_seconds is a strict subset of talk_seconds
          // (same rows, `duration >= 60`), but clamp anyway so a future change to
          // either window can't render "-12m".
          { label: "Voicemails & hang-ups (under 1 min)", secs: Math.max(0, talk - convo), level: 1 },
        ];
      })()
    : [];
  // Same talk time, cut the other way. Shown in the same box so nobody has to
  // cross-reference the Dials tile to find out how much was inbound — and
  // because "on the dialer" deliberately excludes inbound, seeing the inbound
  // share here is what explains a small dialer number next to a big talk number.
  // A bare conversation count says nothing on its own. Average length answers
  // "were these real appointments or polite brush-offs", and dials-per-conversation
  // answers "what did it cost to get one" — the two things an owner actually asks
  // after seeing the number.
  const conversationRows = (() => {
    if (!insightCore) return undefined;
    const n = Number(insightCore.conversations || 0);
    if (n === 0) return undefined;
    const secs = Number(insightCore.conversation_seconds || 0);
    const outbound = Number(insightCore.dials_outbound || 0);
    const out: Array<{ label: string; value: string }> = [
      { label: "Average length", value: formatDuration(Math.round(secs / n)) },
      { label: "Total time", value: formatDuration(secs) },
    ];
    if (outbound > 0) {
      out.push({
        label: "Dials per conversation",
        value: Math.round(outbound / n).toLocaleString(),
      });
    }
    return out;
  })();
  const dialsRows = (() => {
    if (!insightCore) return undefined;
    const out: Array<{ label: string; value: string }> = [];
    const inbound = Number(insightCore.dials_inbound || 0);
    if (inbound > 0) out.push({ label: "Inbound received", value: inbound.toLocaleString() });
    const leads = Number(insightCore.unique_leads_dialed || 0);
    if (leads > 0) {
      out.push({ label: "Leads reached out to", value: leads.toLocaleString() });
      const outbound = Number(insightCore.dials_outbound || 0);
      if (outbound > 0) {
        out.push({ label: "Attempts per lead", value: (outbound / leads).toFixed(1) });
      }
    }
    return out.length > 0 ? out : undefined;
  })();
  const directionSplit = insightCore
    ? {
        outbound: Number(insightCore.talk_seconds_outbound || 0),
        inbound: Number(insightCore.talk_seconds_inbound || 0),
      }
    : null;
  // Stuck lines are excluded from every figure above, so say so rather than
  // leaving an owner to wonder why the day looks light. These are real, billed
  // Twilio minutes on a call that never got a hangup — worth surfacing, just
  // not worth counting as selling time.
  const stuckLines = insightCore ? Number(insightCore.stuck_lines || 0) : 0;
  const workingRate =
    insightHygiene && Number(insightHygiene.assigned) > 0
      ? Math.round((Number(insightHygiene.touched) / Number(insightHygiene.assigned)) * 100)
      : 0;

  const agentStats = userStats || {
    id: agencyUser.userId,
    name: agencyUser.user.name,
    email: agencyUser.user.email,
    totalPremium: 0,
    totalPolicies: 0,
    closingRate: 0,
    changePercentage: 0,
    isOnline: false,
    userStatus: "offline" as const,
    dialTime: 0,
    activeLeads: 0,
    assignedLeads: 0,
    totalCalls: 0,
    avgPolicySize: 0,
    totalLeads: 0,
    topProducts: [],
    monthlyTrends: [],
  };

  const status = (agentStats.userStatus || (agentStats.isOnline ? "available" : "offline")) as string;
  const statusMeta = STATUS_META[status] || STATUS_META.offline;

  // Aggregate the agent's topProducts by carrier so the pie shows a clean
  // per-carrier breakdown (a single carrier can appear in topProducts under
  // multiple product names). Drops zero-premium rows so empty slices don't
  // pollute the chart.
  const carrierBreakdown = useMemo(() => {
    const byCarrier = new Map<string, { carrier: string; totalPremium: number; issuedPremium: number; policyCount: number }>();
    for (const p of agentStats.topProducts || []) {
      const carrier = (p as any).carrier?.trim() || "Unknown";
      const premium = Number((p as any).totalPremium) || 0;
      const issued = Number((p as any).issuedPremium) || 0;
      const count = Number((p as any).count) || 0;
      const existing = byCarrier.get(carrier);
      if (existing) {
        existing.totalPremium += premium;
        existing.issuedPremium += issued;
        existing.policyCount += count;
      } else {
        byCarrier.set(carrier, { carrier, totalPremium: premium, issuedPremium: issued, policyCount: count });
      }
    }
    const arr = Array.from(byCarrier.values())
      .filter((c) => c.totalPremium > 0 || c.policyCount > 0)
      .sort((a, b) => b.totalPremium - a.totalPremium);

    const totalPremium = arr.reduce((sum, c) => sum + c.totalPremium, 0);
    return arr.map((c, i) => ({
      ...c,
      percentage: totalPremium > 0 ? (c.totalPremium / totalPremium) * 100 : 0,
      color: CARRIER_COLORS[i % CARRIER_COLORS.length],
    }));
  }, [agentStats.topProducts]);

  // Sold & Approved only. Lives on the API response rather than the local
  // fallback object, so it is read straight off userStats.
  const issuedPremium = Number((userStats as { issuedPremium?: number } | undefined)?.issuedPremium || 0);
  const issuedPolicies = Number(
    (userStats as { issuedPolicies?: number } | undefined)?.issuedPolicies || 0,
  );

  // Average of the SOLD set, since it sits under "Policies Sold". It used to
  // divide production (sold + underwriting) — a different pair of numbers
  // than the headline it was captioning.
  const avgPolicy = issuedPolicies > 0 ? Math.round(issuedPremium / issuedPolicies) : 0;
  const underwritingPolicies = Math.max((agentStats.totalPolicies || 0) - issuedPolicies, 0);
  const underwritingPremium = Math.max((agentStats.totalPremium || 0) - issuedPremium, 0);

  const chartData = (agentStats.monthlyTrends && agentStats.monthlyTrends.length > 0)
    ? agentStats.monthlyTrends
    : [
        { month: "Jan", premium: 0, issuedPremium: 0, policies: 0 },
        { month: "Feb", premium: 0, issuedPremium: 0, policies: 0 },
        { month: "Mar", premium: 0, issuedPremium: 0, policies: 0 },
        { month: "Apr", premium: 0, issuedPremium: 0, policies: 0 },
        { month: "May", premium: 0, issuedPremium: 0, policies: 0 },
        { month: "Jun", premium: 0, issuedPremium: 0, policies: 0 },
      ];

  // ─────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Button onClick={onBack} variant="outline" className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          {backText}
        </Button>
        <Card className="border-rose-200 bg-rose-50">
          <CardContent className="flex items-center justify-center py-12 text-rose-600">
            Failed to load agent stats. Please try again.
          </CardContent>
        </Card>
      </div>
    );
  }

  const formattedPhone = (() => {
    const phone = (userStats as any)?.contactPhone || (userStats as any)?.phone || agencyUser.user.contactPhone || agencyUser.user.phone;
    if (!phone) return null;
    const cleaned = String(phone).replace(/\D/g, "");
    if (cleaned.length >= 10) {
      const last10 = cleaned.slice(-10);
      return `(${last10.slice(0, 3)}) ${last10.slice(3, 6)}-${last10.slice(6, 10)}`;
    }
    return phone;
  })();

  const accountType = (userStats as any)?.accountType || agencyUser.user.accountType;
  const accountTypeLabel =
    accountType === "super_agent" ? "Super Agent" :
    accountType === "full_fledged" ? "Full-Fledged" :
    accountType === "basic" ? "Basic" :
    accountType || "—";

  return (
    <div className="agent-details-root space-y-6">
      {/* ───── Back nav ───── */}
      <Button onClick={onBack} variant="ghost" className="-ml-2 gap-2 text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" />
        {backText}
      </Button>

      {/* ───── Hero ───── */}
      <Card className="overflow-hidden border-0 shadow-sm">
        <div className="bg-gradient-to-br from-indigo-50 via-white to-blue-50 px-6 py-7 sm:px-8">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-4">
              <div className="relative shrink-0">
                {agencyUser.user.profileImageUrl ? (
                  <img
                    src={agencyUser.user.profileImageUrl}
                    alt={agencyUser.user.name}
                    className="h-16 w-16 rounded-full object-cover ring-4 ring-white shadow-sm"
                  />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white ring-4 ring-white shadow-sm">
                    <User className="h-7 w-7 text-indigo-500" />
                  </div>
                )}
                <span className={`absolute bottom-0 right-0 h-4 w-4 rounded-full ring-2 ring-white ${statusMeta.dot}`} />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-2xl font-semibold tracking-tight text-gray-900">
                    {agencyUser.user.name}
                  </h1>
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${statusMeta.bg} ${statusMeta.text}`}>
                    <CircleDot className="h-3 w-3" />
                    {statusMeta.label}
                  </span>
                </div>
                <p className="mt-1 text-sm text-gray-600">
                  <span className="capitalize">{agencyUser.role}</span>
                  {agencyUser.user.title && <span> · {agencyUser.user.title}</span>}
                  <span> · {accountTypeLabel}</span>
                </p>
                {(agencyUser.user.email || formattedPhone) && (
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500">
                    {agencyUser.user.email && (
                      <span className="inline-flex items-center gap-1">
                        <Mail className="h-3 w-3 text-gray-400" />
                        {agencyUser.user.email}
                      </span>
                    )}
                    {formattedPhone && (
                      <span className="inline-flex items-center gap-1">
                        <Phone className="h-3 w-3 text-gray-400" />
                        {formattedPhone}
                      </span>
                    )}
                  </p>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 gap-1.5 bg-white/70"
              onClick={() => setAccountOpen((v) => !v)}
            >
              <BadgeInfo className="h-4 w-4" />
              Account Info
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${accountOpen ? "rotate-180" : ""}`} />
            </Button>
          </div>

          {/* Inline account panel — expands ABOVE the stat tiles, right under
              the header. Mounts only when expanded, so the fetch stays lazy. */}
          {accountOpen && (
            <AgentAccountPanel
              agencyId={agencyId}
              agentId={agencyUser.userId}
              period={period}
              startDate={startDate}
              endDate={endDate}
            />
          )}

          {/* Headline stat strip — sales · calling · messaging (3×3) */}
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <HeadlineStat
              icon={<DollarSign className="h-3.5 w-3.5" />}
              label="Total Premium Sold"
              value={`$${(issuedPremium || 0).toLocaleString()}`}
              delta={agentStats.changePercentage}
              extra={{ label: "Production", value: `$${(agentStats.totalPremium || 0).toLocaleString()}` }}
              tip={`Total Premium Sold counts only policies on leads marked Sold & Approved. Production adds the $${underwritingPremium.toLocaleString()} on leads still in Underwriting.`}
            />
            <HeadlineStat
              icon={<FileText className="h-3.5 w-3.5" />}
              label="Policies Sold"
              value={issuedPolicies.toLocaleString()}
              sub={avgPolicy > 0 ? `avg $${avgPolicy.toLocaleString()}` : undefined}
              extra={{ label: "Total", value: `${(agentStats.totalPolicies || 0).toLocaleString()} · ${underwritingPolicies.toLocaleString()} in underwriting` }}
              tip="Policies Sold counts only policies on leads marked Sold & Approved. Total adds policies whose lead is still in Underwriting. The average is per sold policy."
            />
            <HeadlineStat
              icon={<Target className="h-3.5 w-3.5" />}
              label="Close Rate"
              value={`${(agentStats.closingRate || 0).toFixed(1)}%`}
              tip="Production closing ratio = (Sold & Approved + Underwriting) ÷ (issued + underwriting + lost). Only leads that reached a decision count; bad numbers and DNCs are excluded as disqualified, not counted as losses."
            />
            <TimeBreakdownStat
              rows={timeRows}
              directionSplit={directionSplit}
              stuckLines={stuckLines}
              fallback={formatDuration(agentStats.dialTime || 0)}
            />
            <HeadlineStat
              icon={<Phone className="h-3.5 w-3.5" />}
              label="Dials"
              value={insightCore ? Number(insightCore.dials_outbound || 0).toLocaleString() : "—"}
              sub="calls placed"
              rows={dialsRows}
              tip="Calls this agent placed. Inbound is listed separately rather than added in — someone calling you is not a dial, and counting it here made this number and the pickup rate read high."
            />
            <HeadlineStat
              icon={<Headset className="h-3.5 w-3.5" />}
              label="Conversations"
              value={insightCore ? Number(insightCore.conversations).toLocaleString() : "—"}
              sub="1 minute or longer"
              rows={conversationRows}
              tip="Calls that ran a minute or more — where someone actually talked to you. Average length says whether they were real conversations; dials per conversation says what it took to get one."
            />
            <HeadlineStat
              icon={<PhoneCall className="h-3.5 w-3.5" />}
              label="Pickup Rate"
              value={insightCore ? `${answerRate}%` : "—"}
              sub={insightCore ? `${conversationRate}% reached a conversation` : undefined}
              tip="Of the calls this agent placed, how many carried any audio at all — an answering machine picking up counts as a pickup. The line below is the honest one: how many became a conversation of a minute or more."
            />
            <HeadlineStat
              icon={<MessageSquareText className="h-3.5 w-3.5" />}
              label="SMS Sent"
              value={insightSms ? Number(insightSms.sms_sent).toLocaleString() : "—"}
              sub={insightSms ? `${Number(insightSms.sms_received).toLocaleString()} received · ${Number(insightSms.sms_leads).toLocaleString()} leads texted` : undefined}
            />
            {/* Distinct leads this agent actually worked by ANY channel — call,
                note, SMS, appointment or door knock — regardless of assignment.
                Previously this showed touched/assigned, which reads "100%" for a
                setter with 1 assigned lead who has really worked hundreds. */}
            <HeadlineStat
              icon={<Flame className="h-3.5 w-3.5" />}
              label="Leads Worked On"
              value={
                insightHygiene && insightHygiene.worked_on != null
                  ? Number(insightHygiene.worked_on).toLocaleString()
                  : insightHygiene
                    ? `${workingRate}%`
                    : "—"
              }
              sub={
                insightHygiene
                  ? `${Number(insightHygiene.touched ?? 0).toLocaleString()}/${Number(insightHygiene.assigned ?? 0).toLocaleString()} of their assigned · any channel`
                  : undefined
              }
            />
          </div>

        </div>
      </Card>

      {/* ───── Tabs: Overview | Assigned Leads ───── */}
      <div className="flex gap-1 border-b border-gray-200">
        {([
          { key: "overview", label: "Overview" },
          { key: "leads", label: "Assigned" },
        ] as const).map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setActiveTab(t.key)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === t.key
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "leads" ? (
        <AgentAssignedLeads agencyId={agencyId} agentId={agencyUser.userId} agentName={agencyUser.user.name} />
      ) : (
      <>
      {/* Quote & Enroll — pinned right under the profile hero so the
          revenue-relevant view (commissions, cases, provisioning) leads. */}
      {quoteEnrollEnabled && agencyUser.userId && (
        <TechficientAgentPanel userId={agencyUser.userId} />
      )}

      {/* ───── Lead Funnel + Communication ───── */}
      <div className="grid gap-4 lg:grid-cols-3">
        {/* Lead Overview shows everything at natural height; the recordings
            card stretches to match and scrolls internally */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4 text-indigo-500" />
              Lead Overview
            </CardTitle>
            <CardDescription className="text-xs">
              Pipelines, lead health, movement &amp; sources — everything on this agent&apos;s assigned leads
            </CardDescription>
          </CardHeader>
          <CardContent className="pb-5">
            {(() => {
              // Real pipeline from the agent's CURRENT assigned book (same
              // source as the hero's Leads Worked On + the Assigned tab).
              const rows = insights?.pipeline || [];
              const totalAssigned = rows.reduce((s, r) => s + Number(r.count || 0), 0);
              if (totalAssigned === 0) {
                return <p className="py-8 text-center text-sm text-gray-400">No leads assigned to this agent yet</p>;
              }
              const byStage = new Map(rows.map((r) => [r.stage.toLowerCase(), Number(r.count) || 0]));
              const CORE = [
                { key: "new lead", label: "Leads", bar: "bg-blue-500" },
                { key: "appt", label: "Appt", bar: "bg-cyan-500" },
                { key: "underwriting", label: "Underwriting", bar: "bg-amber-500" },
                { key: "sold & approved", label: "Sold & Approved", bar: "bg-emerald-500" },
              ];
              const coreKeys = new Set(CORE.map((c) => c.key));
              // terminal/closed-out stages stay as chips; every OTHER stage is a
              // custom pipeline and gets its own bar (NOT folded into New lead)
              const TERMINAL = new Set(["no sale", "do not call", "dnc", "do not contact", "bad number", "bad #", "declined", "lapsed", "disposed"]);
              const customPipelines = rows.filter((r) => !coreKeys.has(r.stage.toLowerCase()) && !TERMINAL.has(r.stage.toLowerCase()));
              const closedOut = rows.filter((r) => TERMINAL.has(r.stage.toLowerCase()));
              const maxCount = Math.max(1, ...rows.map((r) => Number(r.count) || 0));
              // avg days spent in each stage (from audit-derived stage flow)
              const avgDays = new Map(
                (insights?.stageFlow?.avgDaysInStage || []).map((d) => [d.stage.toLowerCase(), d.avgDays])
              );
              return (
                <div className="space-y-4">
                  <div className="flex items-baseline justify-between text-xs text-gray-500">
                    <span>
                      <span className="text-lg font-semibold text-gray-900 tabular-nums">{totalAssigned.toLocaleString()}</span>{" "}
                      leads currently assigned
                    </span>

                  </div>
                  <div className="space-y-2.5">
                    {(() => {
                      // "Leads" = new leads + everything sitting in custom pipelines
                      // (still working leads pre-Appt); custom pipelines render as
                      // an indented breakdown of that bar, not separate stages
                      const customTotal = customPipelines.reduce((sum, o) => sum + (Number(o.count) || 0), 0);
                      const leadsCombined = (byStage.get("new lead") || 0) + customTotal;
                      const barMax = Math.max(maxCount, leadsCombined, 1);
                      const rows2: { label: string; count: number; bar: string; sub?: boolean }[] = [
                        { label: "Leads", count: leadsCombined, bar: "bg-blue-500" },
                        ...customPipelines.map((o) => ({ label: o.stage, count: Number(o.count) || 0, bar: "bg-indigo-300", sub: true })),
                        ...CORE.filter((c) => c.key !== "new lead").map((c) => ({ label: c.label, count: byStage.get(c.key) || 0, bar: c.bar })),
                      ];
                      return rows2.map((c) => {
                        const pct = totalAssigned > 0 ? (c.count / totalAssigned) * 100 : 0;
                        const dayKey = c.label === "Leads" ? "new lead" : c.label.toLowerCase();
                        return (
                          <div key={c.label} className={`flex items-center gap-3 ${c.sub ? "pl-5" : ""}`}>
                            <span className={`flex-shrink-0 truncate text-xs ${c.sub ? "w-[156px] text-gray-400" : "w-44 text-gray-600"}`} title={c.label}>
                              {c.sub && <span className="mr-1 text-gray-300">↳</span>}
                              {c.label}
                              {avgDays.has(dayKey) && !c.sub && (
                                <span className="ml-1.5 rounded bg-gray-100 px-1 py-px text-[9px] font-medium text-gray-500">
                                  ~{(() => { const d = avgDays.get(dayKey)!; return d < 1 ? "<1" : d; })()}d avg
                                </span>
                              )}

                            </span>
                            <div className={`flex-1 overflow-hidden rounded-full bg-gray-100 ${c.sub ? "h-3.5" : "h-6"}`}>
                              <div
                                className={`h-full rounded-full ${c.bar} transition-all`}
                                style={{ width: `${c.count > 0 ? Math.max(2, (c.count / barMax) * 100) : 0}%` }}
                              />
                            </div>
                            <span className="w-24 flex-shrink-0 text-right text-xs tabular-nums text-gray-700">
                              <span className={c.sub ? "" : "font-semibold"}>{c.count.toLocaleString()}</span> · {pct.toFixed(0)}%
                            </span>
                          </div>
                        );
                      });
                    })()}
                  </div>
                  {(() => {
                    // funnel progression on the current book — how deep leads get
                    const appt = byStage.get("appt") || 0;
                    const uw = byStage.get("underwriting") || 0;
                    const sold = byStage.get("sold & approved") || 0;
                    const reachedAppt = appt + uw + sold;
                    const reachedUw = uw + sold;
                    const pct = (n: number) => (totalAssigned > 0 ? `${((n / totalAssigned) * 100).toFixed(0)}%` : "—");
                    // cumulative funnel depth — "or beyond" so it never matches
                    // the per-stage bars above (Appt bar = sitting in Appt NOW;
                    // this = ever made it at least that far)
                    const stats = [
                      { label: "Reached Appt or beyond", value: pct(reachedAppt), sub: `${appt.toLocaleString()} in Appt + ${uw.toLocaleString()} UW + ${sold.toLocaleString()} sold`, tone: "text-cyan-600" },
                      { label: "Reached Underwriting or beyond", value: pct(reachedUw), sub: `${uw.toLocaleString()} in UW + ${sold.toLocaleString()} sold`, tone: "text-amber-600" },
                      {
                        label: "Sold",
                        value: pct(sold),
                        sub: `${sold.toLocaleString()} lead${sold === 1 ? "" : "s"}${
                          insights?.stageFlow?.avgDaysToClose != null ? ` · ~${Math.round(insights.stageFlow.avgDaysToClose)}d to close` : ""
                        }`,
                        tone: "text-emerald-600",
                      },
                    ];
                    return (
                      <div className="grid grid-cols-3 gap-3 border-t pt-4">
                        {stats.map((st) => (
                          <div key={st.label} className="rounded-lg bg-gray-50 px-3 py-2.5 text-center">
                            <p className={`text-xl font-semibold tabular-nums ${st.tone}`}>{st.value}</p>
                            <p className="mt-0.5 text-[11px] font-medium text-gray-600">{st.label}</p>
                            <p className="text-[10px] text-gray-400">{st.sub}</p>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                  {/* Lead health (absorbed the old Lead Hygiene card) */}
                  {insightHygiene && (() => {
                    const assigned = Number(insightHygiene.assigned) || 0;
                    const touched = Number(insightHygiene.touched) || 0;
                    const touchedPct = assigned > 0 ? (touched / assigned) * 100 : 0;
                    const pctOf = (n: number) => (assigned > 0 ? `${((n / assigned) * 100).toFixed(0)}% of leads` : "");
                    const tiles = [
                      { label: "Never called", count: Number(insightHygiene.never_called) || 0, icon: PhoneOff, tone: "text-amber-600", bg: "bg-amber-50 ring-amber-600/10" },
                      { label: "Stale 7d+", count: Number(insightHygiene.stale_7d) || 0, icon: Clock3, tone: "text-rose-600", bg: "bg-rose-50 ring-rose-600/10" },
                      { label: "No next step", count: Number(insightHygiene.missing_next_step) || 0, icon: CalendarX, tone: "text-orange-600", bg: "bg-orange-50 ring-orange-600/10" },
                    ];
                    return (
                      <div className="border-t pt-3">
                        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">Lead health</p>
                        <div className="mb-1.5 flex items-center justify-between text-xs">
                          <span className="text-gray-500">Coverage</span>
                          <span className="font-semibold tabular-nums text-emerald-600">{touchedPct.toFixed(0)}% touched</span>
                        </div>
                        <div className="flex h-3 w-full overflow-hidden rounded-full bg-gray-100">
                          <div className="h-full rounded-l-full bg-emerald-500 transition-all" style={{ width: `${touchedPct}%` }} />
                        </div>
                        <div className="mt-1 flex justify-between text-[10px] text-gray-400">
                          <span>{touched.toLocaleString()} touched</span>
                          <span>{(assigned - touched).toLocaleString()} untouched</span>
                        </div>
                        <div className="mt-2.5 grid grid-cols-3 gap-2">
                          {tiles.map((t) => (
                            <div key={t.label} className={`rounded-lg p-2.5 text-center ring-1 ring-inset ${t.count > 0 ? t.bg : "bg-gray-50 ring-gray-500/5"}`}>
                              <t.icon className={`mx-auto h-4 w-4 ${t.count > 0 ? t.tone : "text-gray-300"}`} />
                              <p className={`mt-1 text-lg font-bold tabular-nums ${t.count > 0 ? t.tone : "text-gray-300"}`}>{t.count.toLocaleString()}</p>
                              <p className="text-[10px] font-medium text-gray-600">{t.label}</p>
                              <p className="text-[9px] text-gray-400">{t.count > 0 ? pctOf(t.count) : "all clear"}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  {/* Stage movement (absorbed the old Stage Flow card) */}
                  {(insights?.stageFlow?.transitions || []).length > 0 && (
                    <div className="border-t pt-3">
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">Where leads moved · last 180 days</p>
                      <div className="flex flex-wrap gap-1.5">
                        {(insights?.stageFlow?.transitions || []).slice(0, 8).map((t) => {
                          const [from, to] = t.pair.split(" → ");
                          return (
                            <span key={t.pair} className="inline-flex items-center gap-1 rounded-full bg-gray-50 py-0.5 pl-1 pr-2 text-[11px] ring-1 ring-inset ring-gray-500/10">
                              <span className={`rounded-full px-1.5 py-px font-medium ${stagePill(from || "")}`}>{from}</span>
                              <span className="text-gray-400">→</span>
                              <span className={`rounded-full px-1.5 py-px font-medium ${stagePill(to || "")}`}>{to}</span>
                              <span className="ml-0.5 font-semibold tabular-nums text-gray-700">×{t.count}</span>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {/* Lead sources (absorbed) — indigo = book share, emerald = sold */}
                  {(insights?.sources || []).length > 0 && (
                    <div className="border-t pt-3">
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">Lead sources</p>
                      <div className="max-h-44 space-y-2.5 overflow-y-auto pr-1">
                        {(() => {
                          const srcRows = insights?.sources || [];
                          const maxLeads = Math.max(1, ...srcRows.map((r) => Number(r.leads) || 0));
                          return srcRows.map((src) => {
                            const leads = Number(src.leads) || 0;
                            const sold = Number(src.sold) || 0;
                            const premium = Number(src.annual_premium) || 0;
                            const conv = leads > 0 ? (sold / leads) * 100 : 0;
                            return (
                              <div key={src.source}>
                                <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                                  <span className="min-w-0 truncate font-medium text-gray-700" title={src.source}>{src.source}</span>
                                  <span className="flex shrink-0 items-center gap-2 tabular-nums">
                                    <span className="text-gray-400">{leads} leads</span>
                                    {sold > 0 && <span className="font-semibold text-emerald-600">{sold} sold · {conv.toFixed(0)}%</span>}
                                    {premium > 0 && (
                                      <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-600/10">
                                        ${Math.round(premium).toLocaleString()}
                                      </span>
                                    )}
                                  </span>
                                </div>
                                <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-gray-100">
                                  <div className="absolute inset-y-0 left-0 rounded-full bg-indigo-400/70" style={{ width: `${Math.max(2, (leads / maxLeads) * 100)}%` }} />
                                  {sold > 0 && (
                                    <div className="absolute inset-y-0 left-0 rounded-full bg-emerald-500" style={{ width: `${Math.max(2, (sold / maxLeads) * 100)}%` }} />
                                  )}
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}
                  {closedOut.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 border-t pt-3 text-xs">
                      <span className="text-gray-400">Closed out:</span>
                      {closedOut.map((o) => (
                        <span key={o.stage} className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
                          {o.stage} · {Number(o.count).toLocaleString()}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Call Recordings replaced the old Communication rollup. The absolute
            wrapper makes the card fill EXACTLY the row height Lead Overview
            defines — its list scrolls inside and can never stretch the row. */}
        <div className="relative min-h-[480px]">
          <div className="lg:absolute lg:inset-0">
            <AgentCallRecordings agencyId={agencyId} agentId={agencyUser.userId} />
          </div>
        </div>
      </div>

      {/* ───── Production Trend (combined) ───── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Production Trend</CardTitle>
          <CardDescription className="text-xs">
            Production vs premium sold, with policy count, over time
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={{ stroke: "#e5e7eb" }} tickLine={false} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" }}
                formatter={(value, name) => {
                  const v = Number(value) || 0;
                  if (name === "Policies") return [v, name as string];
                  return [`$${Math.round(v).toLocaleString()}`, name as string];
                }}
              />
              <Bar yAxisId="right" dataKey="policies" name="Policies" fill="#a5b4fc" radius={[4, 4, 0, 0]} barSize={20} />
              <Line yAxisId="left" type="monotone" dataKey="premium" name="Total Production" stroke="#4f46e5" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              {/* Sold sits under production by definition, so it reads as a
                  filled band beneath the same line rather than a rival series. */}
              <Line yAxisId="left" type="monotone" dataKey="issuedPremium" name="Total Premium Sold" stroke="#059669" strokeWidth={2} strokeDasharray="4 3" dot={{ r: 2.5 }} activeDot={{ r: 4 }} />
              <Legend verticalAlign="bottom" height={24} iconType="plainline" wrapperStyle={{ fontSize: 11 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* ───── Carriers · Sold & Approved (NEW pie chart) ───── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Production by Carrier</CardTitle>
          <CardDescription className="text-xs">
            Total production per carrier, with the sold and approved portion of each
          </CardDescription>
        </CardHeader>
        <CardContent>
          {carrierBreakdown.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-500">
              No sold or approved policies yet during the selected period.
            </div>
          ) : (
            <div className="grid gap-6 lg:grid-cols-5">
              {/* Pie */}
              <div className="lg:col-span-2">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={carrierBreakdown}
                      dataKey="totalPremium"
                      nameKey="carrier"
                      innerRadius={50}
                      outerRadius={95}
                      paddingAngle={1}
                    >
                      {carrierBreakdown.map((entry, i) => (
                        <Cell key={`cell-${i}`} fill={entry.color} stroke="#fff" strokeWidth={2} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" }}
                      formatter={(value, _name, entry: any) => {
                        const v = Number(value) || 0;
                        const count = entry?.payload?.policyCount ?? 0;
                        const issued = Number(entry?.payload?.issuedPremium) || 0;
                        return [
                          `$${v.toLocaleString()} production · $${issued.toLocaleString()} sold · ${count} ${count === 1 ? "policy" : "policies"}`,
                          entry?.payload?.carrier ?? "",
                        ];
                      }}
                    />
                    <Legend
                      verticalAlign="bottom"
                      align="center"
                      iconType="circle"
                      wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Per-carrier list with bars */}
              <div className="lg:col-span-3 space-y-2.5">
                {carrierBreakdown.map((c) => (
                  <div key={c.carrier} className="group">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="flex items-center gap-2 text-sm font-medium text-gray-900">
                        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: c.color }} />
                        {c.carrier}
                      </span>
                      <span className="shrink-0 whitespace-nowrap text-sm font-semibold tabular-nums text-gray-900">
                        ${Math.round(c.totalPremium).toLocaleString()}
                        <span className="ml-2 text-xs font-medium text-emerald-600">
                          ${Math.round(c.issuedPremium || 0).toLocaleString()} sold
                        </span>
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-3">
                      <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${Math.max(c.percentage, 2)}%`, backgroundColor: c.color }}
                        />
                      </div>
                      <span className="text-[11px] text-gray-500 tabular-nums shrink-0 w-24 text-right">
                        {c.policyCount} {c.policyCount === 1 ? "policy" : "policies"} · {c.percentage.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ───── Top Products (compact) ───── */}
      {agentStats.topProducts && agentStats.topProducts.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Top Products</CardTitle>
            <CardDescription className="text-xs">
              Best-selling individual products by production, with the sold portion
            </CardDescription>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="divide-y divide-gray-100">
              {agentStats.topProducts.slice(0, 5).map((product: any, index: number) => (
                <div
                  key={`${product.product}-${product.carrier}-${index}`}
                  className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-xs font-semibold text-indigo-700">
                      {index + 1}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{product.product}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {product.carrier} · {product.count} {product.count === 1 ? "policy" : "policies"}
                      </p>
                    </div>
                  </div>
                  <p className="shrink-0 whitespace-nowrap text-sm font-semibold tabular-nums text-gray-900">
                    ${Math.round(Number(product.totalPremium)).toLocaleString()}
                    <span className="ml-2 text-xs font-medium text-emerald-600">
                      ${Math.round(Number(product.issuedPremium) || 0).toLocaleString()} sold
                    </span>
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}


      {/* ───── Deep coaching analytics ───── */}
      <AgentPerformanceInsights
        agencyId={agencyId}
        agentId={agencyUser.userId}
        startDate={startDate}
        endDate={endDate}
        period={period}
      />

      {/* ───── Call-effort economics for THIS agent's book ───── */}
      <Card>
        <CardContent className="pt-6">
          <DialEfficiencyTab
            agencyId={agencyId}
            agentId={agencyUser.userId}
            agentName={agencyUser.user?.name}
            startDate={startDate}
            endDate={endDate}
            period={period}
          />
        </CardContent>
      </Card>
      </>
      )}

    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Small presentational helpers — kept inline because they're only used here
// and pulling them out would just add file count without clarity.
// ─────────────────────────────────────────────────────────────────────────

interface AccountDetails {
  subscription: {
    status: string;
    start_date?: string;
    current_period_end?: string;
    trial_end?: string;
    next_billing_at?: string;
    grace_period_ends?: string;
    seat_type?: string;
    coupon_code?: string;
    discount_percent?: string | number;
    plan_name?: string;
    plan_price?: string | number;
    currency?: string;
    interval?: string;
    paid_by?: string;
    billing_agency_name?: string;
  } | null;
  account: { created_at?: string; last_login_at?: string; last_login_ip?: string; last_login_location?: string | null } | null;
  // Daily time-in-CRM, derived server-side from audit + call activity. Numerics come
  // back from pg as strings, hence the string types on blocks/active_hours.
  activity?: {
    day: string;
    first_activity: string | null;
    last_activity: string | null;
    blocks: string | number;
    active_hours: string | number;
    // Call bookends are tracked separately from activity — they answer a different
    // question ("when did dialling start/stop") and measurably differ.
    first_call: string | null;
    last_call: string | null;
    dials: string | number;
  }[];
  activityTimezone?: string;
  // The window the server actually used, echoed back so the label can't drift from
  // the rows it sits above.
  activityRange?: { start: string; end: string };
  sessions: { ip_address?: string; user_agent?: string; is_active?: boolean; last_active?: string; created_at?: string; location?: string | null }[];
  phoneNumbers: {
    phone_number: string;
    status?: string;
    created_at?: string;
    is_free_trial_number?: boolean;
    free_trial_expires_at?: string;
    is_ai_calling?: boolean;
    monthly_price?: string | number;
    next_renewal_at?: string;
    billing_status?: string;
    is_free?: boolean;
  }[];
}

const SUB_STATUS_TONE: Record<string, string> = {
  active: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  trialing: "bg-blue-50 text-blue-700 ring-blue-600/20",
  past_due: "bg-amber-50 text-amber-700 ring-amber-600/20",
  canceled: "bg-red-50 text-red-700 ring-red-600/20",
  cancelled: "bg-red-50 text-red-700 ring-red-600/20",
};

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

const fmtDateTime = (d?: string | null) =>
  d
    ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
    : "—";

// Rough browser/OS from the user agent — enough for "which device is this",
// not a full UA parser.
function describeUserAgent(ua?: string) {
  if (!ua) return "Unknown device";
  const browser = /edg\//i.test(ua) ? "Edge" : /chrome/i.test(ua) ? "Chrome" : /firefox/i.test(ua) ? "Firefox" : /safari/i.test(ua) ? "Safari" : "Browser";
  const os = /iphone|ipad/i.test(ua) ? "iOS" : /android/i.test(ua) ? "Android" : /mac os/i.test(ua) ? "Mac" : /windows/i.test(ua) ? "Windows" : /linux/i.test(ua) ? "Linux" : "";
  return os ? `${browser} · ${os}` : browser;
}

function AccountRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="text-right font-medium text-gray-900">{value}</span>
    </div>
  );
}

function AgentAccountPanel({
  agencyId,
  agentId,
  period,
  startDate,
  endDate,
}: {
  agencyId: string;
  agentId: string;
  // Only the Time-in-CRM block uses these. Subscription, phone numbers and sessions
  // are current-state and stay unfiltered on purpose.
  period: "6-months" | "12-months" | "year-to-date";
  startDate?: string;
  endDate?: string;
}) {
  // Mounted only while expanded, so `enabled` needs no open flag.
  const { data, isLoading } = useQuery<AccountDetails>({
    // Filter values are part of the key — without them react-query would serve the
    // previous range's cached rows when the user changes the date filter.
    queryKey: ["agent-account-details", agencyId, agentId, period, startDate, endDate],
    queryFn: async () => {
      // The viewer's IANA zone. Activity timestamps are stored UTC, so without this
      // the daily buckets straddle midnight and an Eastern agent's evening reads as
      // the next day starting at 00:02.
      const tz =
        Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
      const qs = new URLSearchParams({ tz, period });
      if (startDate) qs.set("startDate", startDate);
      if (endDate) qs.set("endDate", endDate);
      const res: any = await handleApiCall(async () =>
        api.get({
          url: getApiUrl(
            `/agencies/${agencyId}/agents/${agentId}/account-details?${qs.toString()}`,
          ),
          auth: true,
        })
      );
      return (res?.data ?? res) as AccountDetails;
    },
    enabled: !!agencyId && !!agentId,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000, // keep the panel live while it's expanded
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  const sub = data?.subscription;
  const subStatus = (sub?.status || "").toLowerCase();
  const acct = data?.account;
  const price = sub?.plan_price != null && !Number.isNaN(Number(sub.plan_price)) ? Number(sub.plan_price) : null;
  const discount = sub?.discount_percent != null ? Number(sub.discount_percent) : 0;
  // What the owner actually wants to know: when does this seat renew or stop.
  const renewsAt = sub?.next_billing_at || sub?.current_period_end;

  if (isLoading || !data) {
    return (
      <div className="mt-4 grid gap-3 border-t border-gray-200/70 pt-4 sm:grid-cols-2 xl:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-28 rounded-lg bg-white/60 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="mt-4 grid gap-4 border-t border-gray-200/70 pt-4 sm:grid-cols-2 xl:grid-cols-4">
      {/* Subscription */}
      <div>
        <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">
          <CreditCard className="h-3.5 w-3.5" /> Subscription
        </div>
        {sub ? (
          <div className="max-h-48 overflow-y-auto space-y-1.5 rounded-lg border border-gray-100 bg-white/80 p-3">
            <AccountRow
              label="Plan"
              value={
                <span className="inline-flex items-center gap-2">
                  {sub.plan_name || "—"}
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset capitalize ${SUB_STATUS_TONE[subStatus] || "bg-gray-100 text-gray-600 ring-gray-500/20"}`}>
                    {subStatus.replace(/_/g, " ")}
                  </span>
                </span>
              }
            />
            {price != null && (
              <AccountRow label="Price" value={`$${price.toLocaleString()}/${sub.interval === "year" ? "yr" : "mo"}${discount > 0 ? ` · ${discount}% off` : ""}`} />
            )}
            <AccountRow label="Member since" value={fmtDate(sub.start_date || acct?.created_at)} />
            {subStatus === "trialing" && sub.trial_end && <AccountRow label="Trial ends" value={fmtDate(sub.trial_end)} />}
            <AccountRow label={subStatus === "canceled" || subStatus === "cancelled" ? "Access ended" : "Renews"} value={fmtDate(renewsAt)} />
            {sub.grace_period_ends && <AccountRow label="Grace period ends" value={fmtDate(sub.grace_period_ends)} />}
            {sub.paid_by && (
              <AccountRow
                label="Paid by"
                value={sub.paid_by.toLowerCase() === "self" ? "Self" : sub.billing_agency_name || sub.paid_by}
              />
            )}
            {sub.coupon_code && <AccountRow label="Coupon" value={sub.coupon_code} />}
          </div>
        ) : (
          <p className="rounded-lg border border-gray-100 bg-white/80 p-3 text-sm text-gray-400">No subscription on file</p>
        )}
      </div>

      {/* Phone numbers + their billing */}
      <div>
        <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">
          <Phone className="h-3.5 w-3.5" /> Phone Numbers
        </div>
        {(data.phoneNumbers || []).length === 0 ? (
          <p className="rounded-lg border border-gray-100 bg-white/80 p-3 text-sm text-gray-400">No phone numbers purchased</p>
        ) : (
          <div className="max-h-48 overflow-y-auto divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white/80">
            {data.phoneNumbers.map((n, i) => {
              const numStatus = (n.status || "").toLowerCase();
              const monthly = n.monthly_price != null && !Number.isNaN(Number(n.monthly_price)) ? Number(n.monthly_price) : null;
              const billing = n.is_free_trial_number
                ? `Free trial${n.free_trial_expires_at ? ` · expires ${fmtDate(n.free_trial_expires_at)}` : ""}`
                : n.is_free
                  ? "Free"
                  : monthly != null
                    ? `$${monthly.toFixed(2)}/mo${n.next_renewal_at ? ` · renews ${fmtDate(n.next_renewal_at)}` : ""}`
                    : n.billing_status || "—";
              return (
                <div key={i} className="flex items-center justify-between gap-3 p-2.5 text-xs">
                  <div className="min-w-0">
                    <p className="inline-flex items-center gap-2 font-medium text-gray-800">
                      {formatPhoneNumber(n.phone_number)}
                      <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset capitalize ${numStatus === "active" ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20" : "bg-gray-100 text-gray-600 ring-gray-500/20"}`}>
                        {numStatus || "unknown"}
                      </span>
                      {n.is_ai_calling && (
                        <span className="rounded-full bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 ring-1 ring-inset ring-violet-600/20">AI</span>
                      )}
                    </p>
                    <p className="text-[11px] text-gray-500">Purchased {fmtDate(n.created_at)}</p>
                  </div>
                  <p className="shrink-0 text-right text-gray-600">{billing}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Login & security */}
      <div>
        <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">
          <Globe className="h-3.5 w-3.5" /> Login & Security
        </div>
        <div className="space-y-1.5 rounded-lg border border-gray-100 bg-white/80 p-3">
          <AccountRow label="Last login" value={fmtDateTime(acct?.last_login_at)} />
          <AccountRow label="Last IP address" value={<span className="font-mono text-xs">{acct?.last_login_ip || "—"}</span>} />
          <AccountRow label="Location" value={acct?.last_login_location || "—"} />
          <AccountRow label="Account created" value={fmtDate(acct?.created_at)} />
        </div>
      </div>

      {/* Time in CRM + Recent Sessions share ONE grid cell so they stack in the same
          column. As separate children of a 4-column grid, Recent Sessions was the 5th
          item and wrapped to row 2 / column 1 — landing under Subscription, far from
          the activity data it belongs with. Both answer "when and from where has this
          person been working", so they read as one story.
          Time in CRM is first: it answers what owners actually ask ("how much are they
          working, when do they start and stop"), whereas sessions answer "what
          device/IP". */}
      <div className="space-y-4">
      {/* Rendered only when there is activity — an empty grid would imply the agent did
          nothing, when it usually just means the window has no data yet. */}
      {(data?.activity?.length ?? 0) > 0 && (
        <div>
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">
              <Clock className="h-3.5 w-3.5" /> Time in CRM
            </div>
            <span className="text-[10px] text-gray-400">
              {data?.activityTimezone || "UTC"}
            </span>
          </div>

          {(() => {
            const rows = data!.activity!;
            // "22:33:48" -> "10:33 PM".
            //
            // Formatted as plain wall-clock text on purpose. These values were ALREADY
            // converted to the requested timezone server-side (AT TIME ZONE), so feeding
            // them through `new Date(...)` would re-apply the browser's offset and shift
            // every time a second time. Seconds are dropped — nobody reads a start time
            // to the second.
            const fmtClock = (t?: string | null) => {
              if (!t) return "—";
              const [rawH, rawM] = t.split(":");
              const h = Number(rawH);
              if (!Number.isFinite(h)) return t;
              const period = h >= 12 ? "PM" : "AM";
              const h12 = h % 12 === 0 ? 12 : h % 12;
              return `${h12}:${(rawM ?? "00").padStart(2, "0")} ${period}`;
            };
            const totalH = rows.reduce((s, r) => s + Number(r.active_hours || 0), 0);
            const avgH = rows.length ? totalH / rows.length : 0;
            return (
              <>
                <div className="mb-2 grid grid-cols-2 gap-2">
                  <div className="rounded-lg border border-gray-100 bg-white/80 p-2.5">
                    <p className="text-lg font-bold text-gray-900">{totalH.toFixed(1)}h</p>
                    {/* "across N active days", not "last N days" — rows only exist for
                        days with activity, so 7 rows can span three weeks. The old
                        wording also disagreed with the table whenever the list was
                        truncated (summary said 8, table showed 7). */}
                    <p className="text-[11px] text-gray-500">
                      Across {rows.length} active day{rows.length === 1 ? "" : "s"}
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-100 bg-white/80 p-2.5">
                    <p className="text-lg font-bold text-gray-900">{avgH.toFixed(1)}h</p>
                    <p className="text-[11px] text-gray-500">Average per active day</p>
                  </div>
                  {/* States the ACTUAL window, echoed back by the API — never a guess.
                      Every previous version of this label was eventually wrong: "last N
                      days" counted active days not calendar days, and "Rolling 30 days"
                      quietly reached into the previous month. Printing the real range is
                      the only version that can't drift from the data above it. */}
                  <p className="col-span-2 -mt-0.5 text-[10px] text-gray-400">
                    {data?.activityRange
                      ? `${new Date(data.activityRange.start).toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                        })} – ${new Date(data.activityRange.end).toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                        })}`
                      : "Selected range"}
                  </p>
                </div>

                {/* Scrolls rather than truncating. This used to `.slice(0, 7)` while the
                    endpoint returned more — the rest was silently dropped, which reads as
                    "that's all the data" instead of "there's more below". Now a rolling
                    30 days, so the list is genuinely long and scrolling is the point.
                    max-h-56 keeps it a little taller than Recent Sessions beneath it,
                    and the header is sticky so columns stay labelled while scrolling. */}
                <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-100">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 z-10 bg-gray-50 text-gray-500">
                      {/* Two pairs of bookends, labelled distinctly: "In/Out" is the
                          working day (any CRM action or call), "Calls" is the dialling
                          window. They are genuinely different — measured on prod they
                          diverge by up to half an hour, and some days have calls with
                          no CRM activity at all. */}
                      <tr>
                        <th className="px-2 py-1.5 text-left font-medium">Day</th>
                        <th className="px-2 py-1.5 text-left font-medium">In</th>
                        <th className="px-2 py-1.5 text-left font-medium">Out</th>
                        <th className="px-2 py-1.5 text-left font-medium">Calls</th>
                        <th className="px-2 py-1.5 text-right font-medium">Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.day} className="border-t border-gray-100">
                          <td className="px-2.5 py-1.5 text-gray-700">
                            {new Date(`${r.day}T00:00:00`).toLocaleDateString(undefined, {
                              weekday: "short",
                              month: "short",
                              day: "numeric",
                            })}
                          </td>
                          <td className="px-2 py-1.5 tabular-nums text-gray-600">{fmtClock(r.first_activity)}</td>
                          <td className="px-2 py-1.5 tabular-nums text-gray-600">{fmtClock(r.last_activity)}</td>
                          <td className="px-2 py-1.5 tabular-nums text-gray-600">
                            {r.first_call ? (
                              <>
                                {fmtClock(r.first_call)}
                                <span className="text-gray-300"> – </span>
                                {fmtClock(r.last_call)}
                                <span className="ml-1 text-[10px] text-gray-400">
                                  ({Number(r.dials || 0)})
                                </span>
                              </>
                            ) : (
                              <span className="text-gray-300">no calls</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-right font-semibold text-gray-900">
                            {Number(r.active_hours || 0).toFixed(1)}h
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-1.5 text-[10px] leading-relaxed text-gray-400">
                  In/Out is the working day — first and last CRM action or call. Calls shows
                  the dialling window and count. Active is time worked, not time signed in:
                  gaps under 30 minutes count as continuous, and an idle open tab counts for nothing.
                </p>
              </>
            );
          })()}
        </div>
      )}

      {/* Recent sessions */}
      <div>
        <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">
          <MonitorSmartphone className="h-3.5 w-3.5" /> Recent Sessions
        </div>
        {(data.sessions || []).length === 0 ? (
          <p className="rounded-lg border border-gray-100 bg-white/80 p-3 text-sm text-gray-400">No sessions recorded</p>
        ) : (
          <div className="max-h-48 overflow-y-auto divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white/80">
            {data.sessions.map((s, i) => (
              <div key={i} className="flex items-center justify-between gap-3 p-2.5 text-xs">
                <div className="min-w-0">
                  <p className="font-medium text-gray-800">{describeUserAgent(s.user_agent)}</p>
                  <p className="font-mono text-[11px] text-gray-500">{s.ip_address || "IP unknown"}</p>
                  {s.location && <p className="text-[11px] text-gray-500">{s.location}</p>}
                </div>
                <div className="shrink-0 text-right">
                  {s.is_active ? (
                    <span className="mb-0.5 inline-flex items-center gap-1 text-emerald-600"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />Active</span>
                  ) : null}
                  <p className="text-gray-500">{fmtDateTime(s.last_active)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

/**
 * Dial / talk breakdown. Three figures in one tile:
 *   On the dialer          — merged power-dialer session windows (effort)
 *   Talking                — live call time, pickup to hangup (contact)
 *   In real conversations  — the share of that which ran 60s+ (opportunity)
 *
 * They are shown together because each one alone gets misread. This tile used
 * to be a single number labelled "Dial Time" that was in fact talk time, so
 * owners read an agent's 3h as time spent dialing when the real figure was 8h.
 */
function TimeBreakdownStat({
  rows,
  directionSplit = null,
  stuckLines = 0,
  fallback,
}: {
  rows: Array<{ label: string; secs: number; level?: number }>;
  directionSplit?: { outbound: number; inbound: number } | null;
  stuckLines?: number;
  fallback: string;
}) {
  const hasRows = rows.length > 0 && rows.some((r) => r.secs > 0);
  return (
    <div className="flex h-full flex-col rounded-lg border border-gray-100 bg-white px-3 py-2.5 shadow-sm">
      <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-gray-500">
        <Clock className="h-3.5 w-3.5" />
        Dial &amp; Talk Time
        <TooltipProvider delayDuration={100}>
          <UITooltip>
            <TooltipTrigger asChild>
              <button type="button" aria-label="Explanation" className="inline-flex cursor-help items-center">
                <Info className="h-3.5 w-3.5 text-primary/70 transition-colors hover:text-primary" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-64 text-xs normal-case tracking-normal leading-relaxed">
              <p><b>On the dialer</b> — signed in and dialing. Lines that ring together count once. Inbound isn&apos;t dialing, so it isn&apos;t counted.</p>
              <p className="mt-1"><b>Talking</b> — line open, pickup to hangup. Ringing never counts. Inbound is included.</p>
              <p className="mt-1"><b>Real conversations</b> — ran a minute or more.</p>
              <p className="mt-1"><b>Voicemails &amp; hang-ups</b> — connected, but nobody spoke.</p>
            </TooltipContent>
          </UITooltip>
        </TooltipProvider>
      </div>
      {hasRows ? (
        <div className="mt-1.5 space-y-1">
          {rows.map((r) => {
            const child = (r.level ?? 0) > 0;
            return (
              <div
                key={r.label}
                className={`flex items-baseline justify-between gap-2 ${child ? "pl-3" : ""}`}
              >
                <span
                  className={
                    child
                      ? "text-[10.5px] text-gray-400 before:mr-1 before:content-['—']"
                      : "text-[11px] font-medium text-gray-600"
                  }
                >
                  {r.label}
                </span>
                <span
                  className={
                    child
                      ? "text-xs font-medium leading-none text-gray-500"
                      : "text-base font-semibold leading-none text-gray-900"
                  }
                >
                  {formatDuration(r.secs)}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="mt-1 text-lg font-semibold text-gray-900">{fallback}</div>
      )}
      {directionSplit && directionSplit.outbound + directionSplit.inbound > 0 && (
        <div className="mt-1.5 flex items-center justify-between border-t border-gray-100 pt-1.5 text-[10px] text-gray-500">
          <span>
            <span className="text-gray-400">Outbound</span>{" "}
            <span className="font-medium text-gray-700">{formatDuration(directionSplit.outbound)}</span>
          </span>
          <span>
            <span className="text-gray-400">Inbound</span>{" "}
            <span className="font-medium text-gray-700">{formatDuration(directionSplit.inbound)}</span>
          </span>
        </div>
      )}
      {stuckLines > 0 && (
        <p className="mt-1.5 text-[10px] leading-snug text-amber-600">
          {stuckLines} stuck {stuckLines === 1 ? "line" : "lines"} excluded — a call that never
          hung up and stayed open for hours. Billed, but not selling time.
        </p>
      )}
    </div>
  );
}

function HeadlineStat({
  icon,
  label,
  value,
  delta,
  sub,
  tip,
  extra,
  rows,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  /** null = no prior period to compare against (rendered as such, not as +100%). */
  delta?: number | null;
  sub?: string;
  tip?: string;
  /** Second figure shown on the delta line — e.g. production beside sold. */
  extra?: { label: string; value: string };
  /** Supporting label/value rows pinned to the bottom of the tile. */
  rows?: Array<{ label: string; value: string }>;
}) {
  const showDelta = typeof delta === "number" && !Number.isNaN(delta);
  return (
    <div className="flex h-full flex-col rounded-lg border border-gray-100 bg-white px-3 py-2.5 shadow-sm">
      <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-gray-500">
        {icon}
        {label}
        {tip && (
          <TooltipProvider delayDuration={100}>
            <UITooltip>
              <TooltipTrigger asChild>
                <button type="button" aria-label="Explanation" className="inline-flex cursor-help items-center">
                  <Info className="h-3.5 w-3.5 text-primary/70 transition-colors hover:text-primary" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-64 text-xs normal-case tracking-normal">{tip}</TooltipContent>
            </UITooltip>
          </TooltipProvider>
        )}
      </div>
      <p className="mt-1 text-xl font-semibold tabular-nums text-gray-900">{value}</p>
      <div className="mt-0.5 flex items-baseline justify-between gap-2">
        {showDelta ? (
          <p className={`flex items-center gap-1 text-[11px] font-medium ${delta! >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
            {delta! >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {delta! >= 0 ? "+" : ""}{delta!.toFixed(1)}% vs prev
          </p>
        ) : delta === null ? (
          <p className="text-[11px] text-gray-400">No prior period</p>
        ) : sub ? (
          <p className="truncate text-[11px] text-gray-500">{sub}</p>
        ) : (
          <p className="text-[11px] text-gray-400">&nbsp;</p>
        )}
        {extra && (
          <span className="shrink-0 whitespace-nowrap text-[11px] normal-case tracking-normal text-gray-500">
            {extra.label}{" "}
            <span className="font-semibold tabular-nums text-gray-900">{extra.value}</span>
          </span>
        )}
      </div>
      {/* Supporting rows. These tiles share a grid row with Dial & Talk Time,
          which is six rows tall — so a tile holding one number either stretched
          with dead space under it or, once centred, left the number floating in
          the middle of an empty box. Both read as broken. Filling the space with
          figures that are actually worth reading is the only version that looks
          deliberate. mt-auto pins them to the bottom so the headline still sits
          where the eye expects it. */}
      {rows && rows.length > 0 && (
        <div className="mt-auto space-y-1 border-t border-gray-100 pt-1.5 normal-case tracking-normal">
          {rows.map((r) => (
            <div key={r.label} className="flex items-baseline justify-between gap-2">
              <span className="text-[10.5px] text-gray-400">{r.label}</span>
              <span className="text-[11px] font-medium tabular-nums text-gray-700">{r.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



/**
 * Takes SECONDS. The backend used to round to whole minutes before sending,
 * which is why five single-day filters could disagree with the five-day range
 * by a minute or two — each day rounded independently, and the errors stacked.
 * Rounding happens once, here, at the edge.
 */
function formatDuration(totalSeconds: number): string {
  if (!totalSeconds || totalSeconds < 60) {
    return !totalSeconds || totalSeconds < 1 ? "0m" : `${Math.round(totalSeconds)}s`;
  }
  const totalMinutes = Math.round(totalSeconds / 60);
  if (totalMinutes >= 60) {
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  return `${totalMinutes}m`;
}
